import { useState, useEffect, useCallback } from 'react';
import {
    collection, query, where, onSnapshot,
    addDoc, updateDoc, doc, serverTimestamp, getDoc, getDocs, increment
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/features/auth/AuthProvider';
import type { Conversation } from '@/types';

/**
 * Hook for managing messaging (conversations and messages).
 */
export const useMessaging = () => {
    const { currentUser } = useAuth();
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [loadingConversations, setLoadingConversations] = useState(true);

    // Clear state on logout
    useEffect(() => {
        if (!currentUser) {
            // eslint-disable-next-line
            setConversations([]);
            setLoadingConversations(false);
        }
    }, [currentUser]);

    // Subscribe to user's conversations
    useEffect(() => {
        if (!currentUser) return;

        // eslint-disable-next-line
        setLoadingConversations(true);

        const q = query(
            collection(db, 'conversations'),
            where('participants', 'array-contains', currentUser.uid)
            // Removed orderBy('updatedAt', 'desc') to avoid missing index errors
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const rawConvos = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                updatedAt: doc.data().updatedAt?.toDate() || new Date(),
                lastMessageAt: doc.data().lastMessageAt?.toDate()
            })) as Conversation[];

            // Client-side Deduplication & Sorting
            // 1. Sort by lastMessageAt/updatedAt desc
            rawConvos.sort((a, b) => {
                const timeA = a.lastMessageAt?.getTime() || a.updatedAt?.getTime() || 0;
                const timeB = b.lastMessageAt?.getTime() || b.updatedAt?.getTime() || 0;
                return timeB - timeA;
            });

            // 2. Group by "Other User" to ensure unique conversation per user
            const uniqueMap = new Map<string, Conversation>();

            rawConvos.forEach(conv => {
                const otherId = conv.participants.find(p => p !== currentUser.uid) || 'self';
                // Since we sorted by newest first, the first one we see is the latest
                if (!uniqueMap.has(otherId)) {
                    uniqueMap.set(otherId, conv);
                }
            });

            setConversations(Array.from(uniqueMap.values()));
            setLoadingConversations(false);
        }, (err) => {
            console.error('Error fetching conversations:', err);
            setLoadingConversations(false);
        });

        return () => unsubscribe();
    }, [currentUser]);

    /**
     * Start or Get existing conversation with a user.
     * @param targetUserId ID of the user to start conversation with.
     */
    /**
     * Start or Get existing conversation with a user.
     * @param targetUserId ID of the user to start conversation with.
     */
    const startConversation = async (targetUserId: string) => {
        if (!currentUser) return null;

        try {
            // 1. Check if conversation already exists (Query Server)
            // We query the server directly to avoid race conditions with local state
            const q = query(
                collection(db, 'conversations'),
                where('participants', 'array-contains', currentUser.uid)
            );

            const snapshot = await getDocs(q);

            // Find all matching conversations
            const matches = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() } as Conversation))
                .filter(data =>
                    data.participants.includes(targetUserId) &&
                    data.participants.length === 2
                );

            // Sort by most recent to ensure we pick the active one matching UI logic
            matches.sort((a, b) => {
                // Handle both Firestore Timestamp (has seconds) and JS Date (has getTime)
                // @ts-ignore - handling raw firestore data mixed with types
                const timeA = a.updatedAt?.seconds ? a.updatedAt.seconds : (a.updatedAt?.getTime ? a.updatedAt.getTime() / 1000 : 0);
                // @ts-ignore
                const timeB = b.updatedAt?.seconds ? b.updatedAt.seconds : (b.updatedAt?.getTime ? b.updatedAt.getTime() / 1000 : 0);
                return timeB - timeA;
            });

            if (matches.length > 0) {
                return matches[0].id;
            }

            // 2. Create new conversation if none exists
            const newConv = {
                participants: [currentUser.uid, targetUserId],
                unreadCounts: {
                    [currentUser.uid]: 0,
                    [targetUserId]: 0
                },
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                lastMessage: null,
                lastMessageAt: null
            };

            const docRef = await addDoc(collection(db, 'conversations'), newConv);
            return docRef.id;
        } catch (error) {
            console.error('Error starting conversation:', error);
            return null;
        }
    };

    /**
     * Sends a message to a conversation.
     */
    const sendMessage = useCallback(async (conversationId: string, content: string) => {
        if (!currentUser) return;
        if (!content.trim()) return;

        try {
            // 1. Add Message
            // FIX: Removed spaces in path string
            await addDoc(collection(db, `conversations/${conversationId}/messages`), {
                conversationId,
                senderId: currentUser.uid,
                content,
                createdAt: serverTimestamp(),
                readBy: [currentUser.uid]
            });

            // 2. Update Conversation
            const convRef = doc(db, 'conversations', conversationId);

            // Fetch clean conversation data to get participants
            // We need this to know WHO to increment count for (everyone except sender)
            const convSnap = await getDoc(convRef);
            if (!convSnap.exists()) return;
            const data = convSnap.data() as Conversation;
            const otherIds = data.participants.filter(pid => pid !== currentUser.uid);

            const updates: Record<string, unknown> = {
                lastMessage: content,
                lastSenderId: currentUser.uid,
                updatedAt: serverTimestamp(),
                lastMessageAt: serverTimestamp()
            };

            // Increment unread for others using atomic increment
            otherIds.forEach(pid => {
                updates[`unreadCounts.${pid}`] = increment(1);
            });



            await updateDoc(convRef, updates);

        } catch (err) {
            console.error('Error sending message:', err);
            throw err;
        }
    }, [currentUser]);

    /**
     * Marks a conversation as read for the current user.
     */
    const markAsRead = useCallback(async (conversationId: string) => {
        if (!currentUser) return;
        const convRef = doc(db, 'conversations', conversationId);
        await updateDoc(convRef, {
            [`unreadCounts.${currentUser.uid}`]: 0
        });
    }, [currentUser]);

    return {
        conversations,
        loadingConversations,
        startConversation,
        sendMessage,
        markAsRead
    };
};
